import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useRevealOnce } from '@/hooks/useRevealOnce';
import { gridContainerVariants, getGridItemVariants } from '@/lib/animations';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { BackgroundGradient } from '@/components/lib/aceternity/BackgroundGradient';
import { InteractiveGridPattern } from '@/components/ui/interactive-grid-pattern';
import { cn } from '@/lib/utils';

export function HomeFounderStrip() {
  const contentReveal = useRevealOnce({ amount: 0.25 });
  const prefersReducedMotion = useReducedMotion();

  return (
    <section
      id="founder-strip"
      className="relative bg-white py-12 md:py-16 overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-radial from-white via-white to-slate-50" />
      <InteractiveGridPattern
        className={cn(
          "[mask-image:radial-gradient(600px_circle_at_center,transparent,rgba(255,255,255,0.3)_70%,white)]",
          "inset-x-0 inset-y-[-30%] h-[200%] skew-y-12",
        )}
      />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-6xl mx-auto">
          <motion.div
            ref={contentReveal.ref}
            className="flex flex-col md:flex-row items-center md:items-start gap-8 md:gap-12"
            variants={prefersReducedMotion ? {} : gridContainerVariants}
            initial="hidden"
            animate={contentReveal.controls}
          >
            {/* Left: Avatar/Photo with hover scale and Background Gradient */}
            <motion.div
              variants={prefersReducedMotion ? {} : getGridItemVariants(0)}
              className="flex-shrink-0"
              whileHover={prefersReducedMotion ? {} : { scale: 1.1 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            >
              <BackgroundGradient
                className="w-24 h-24 md:w-32 md:h-32 flex items-center justify-center overflow-hidden"
                animate={!prefersReducedMotion}
              >
                <img
                  src="/media/services/adam-avatar.png"
                  alt="Adam, founder of Attain Domains"
                  className="w-full h-full rounded-full object-cover"
                />
              </BackgroundGradient>
            </motion.div>

            {/* Right: Content */}
            <motion.div
              variants={prefersReducedMotion ? {} : getGridItemVariants(1)}
              className="flex-1 text-center"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-slate-900">
                Built and run by an operator, not a committee.
              </h2>
              
              <div className="space-y-4 mb-8 text-lg md:text-xl text-slate-600 leading-relaxed max-w-3xl mx-auto">
                <p>
                  I've been starting, running, and selling online service businesses for over 20 years. I know what works and what doesn't. My goal is to help you be successful. I work with a limited amount of clients at a time.
                </p>
              </div>

              <div className="flex justify-center">
                <Link
                  to="/about"
                  className="liquid-button-view-details relative inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-base md:text-lg font-semibold cursor-pointer overflow-hidden px-5 py-3 text-[#0000FF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0000FF] focus-visible:ring-offset-2 transition-transform duration-200"
                  style={{
                    background:
                      'linear-gradient(180deg, #0000FF 0%, #000000 100%) no-repeat calc(200% - var(--liquid-button-fill, 0%)) 100% / 200% var(--liquid-button-fill, 0.2em)',
                    transition:
                      'background 0.3s var(--liquid-button-delay, 0s), color 0.3s var(--liquid-button-delay, 0s), background-position 0.3s calc(0.3s - var(--liquid-button-delay, 0s)), transform 0.2s ease-out',
                    '--liquid-button-fill': '0%',
                    '--liquid-button-delay': '0s',
                  } as React.CSSProperties & { '--liquid-button-fill'?: string; '--liquid-button-delay'?: string }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.setProperty('--liquid-button-fill', '100%');
                    e.currentTarget.style.setProperty('--liquid-button-delay', '0.3s');
                    e.currentTarget.style.color = '#FFFFFF';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.setProperty('--liquid-button-fill', '0%');
                    e.currentTarget.style.setProperty('--liquid-button-delay', '0s');
                    e.currentTarget.style.color = '#0000FF';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  Learn who I am
                </Link>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
